/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  SystemProp,
  Theme,
  ThemeSize,
  styled
} from '@xstyled/styled-components';
import * as CSS from 'csstype';
import React, {
  Children,
  Component,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { animated, useSpring } from '@react-spring/web';
import { useSequenceStore } from '~/training/sequence/SequenceStore';
import { Card } from '~/ui/card/Card';
import {
  Item,
  ItemDefinition,
  ItemValidationError,
  registry,
  RenderProps
} from '../Item';
import { FLIP_BACK, FLIP_CARD, FLIP_FRONT } from '../itemIds';
import { getEditorState, ServerItemResponse } from '~/shell/Store';
import { FrontCard } from './components/FrontCard';
import { FrontWrapper } from './components/FrontWrapper';
import audioEffect from '~/assets/mp3/card_flip.mp3';
import { useAudioEffect } from '~/ui/audio-effect/useAudioEffect';
import { isIPhone13, isIPad13 } from 'react-device-detect';
import { t } from 'ttag';
import EditIcon from '~/assets/icons/editIconBig.svg';
import DuplicateIcon from '~/assets/icons/copy.svg';
import TrashIcon from '~/assets/icons/trashIcon.svg';
import { useEditorStore } from '~/shell/Store';
import { useReorderCards } from './useReorderCards';
import FlipCardEditModal, { FlipCardTitleLabels } from './FlipCardEditModal';
import { useToastStore } from '~/ui/toast/toastStore';
import { ToastSeverity } from '~/ui/toast/types';
import { stripHtml } from '~/lib/sanitization';

const AnimatedCard = animated(Card);

const StyledCard = styled.div<{
  width?: SystemProp<ThemeSize<Theme> | CSS.Property.Width, Theme>;
  height?: SystemProp<ThemeSize<Theme> | CSS.Property.Height, Theme>;
  editor?: boolean;
}>`
  display: grid;
  width: ${p => p.width};
  height: ${p => p.height};
  min-width: 200px;
  min-height: 200px;
  grid-template-columns: 100%;
  grid-template-rows: 100%;
  background: transparent;
  position: relative;

  &:focus-visible {
    border-radius: 20px;
    outline: 2px solid var(--color-focus-ring);
    outline-offset: 2px;
    z-index: 2;
  }

  &:focus-visible::after {
    content: '';
    display: block;
    position: absolute;
    border-radius: 20px;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    border: 2px solid white;
    pointer-events: none;
  }

  ${p =>
    p.editor &&
    `
    &:hover, &:hover * {
      cursor: pointer;
    }
  `}

  ${p =>
    !p.editor &&
    `
    /* Prevent image drag ghost in learner mode */
    img { -webkit-user-drag: none; user-drag: none; }
  `}

  @media (max-width: sm) {
    width: 95%;
  }
`;

/**
 * When the card is rendered as an `<li>`, the button semantics live on this
 * inner wrapper: `role="button"` is not allowed on a list item and strips its
 * `listitem` semantics, breaking the parent `<ul>` (axe `list` /
 * `aria-allowed-role`). Mirrors the grid of {@link StyledCard} so the two
 * faces keep stacking in the same cell.
 */
const CardInteractive = styled.div`
  display: grid;
  width: 100%;
  height: 100%;
  grid-template-columns: 100%;
  grid-template-rows: 100%;
  background: transparent;
  position: relative;

  &:focus-visible {
    border-radius: 20px;
    outline: 2px solid var(--color-focus-ring);
    outline-offset: 2px;
    z-index: 2;
  }

  &:focus-visible::after {
    content: '';
    display: block;
    position: absolute;
    border-radius: 20px;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    border: 2px solid white;
    pointer-events: none;
  }
`;

interface Props {
  background?: string;
  width?: SystemProp<ThemeSize<Theme> | CSS.Property.Width, Theme>;
  height?: SystemProp<ThemeSize<Theme> | CSS.Property.Height, Theme>;
  correct?: boolean;
  type?: string;
  separateTitleAndBody?: boolean;
  showSubtitle?: boolean;
  showTitle?: boolean;
  /** The card's own title is not its body's heading (see FlipCardEditModal). */
  cardOwnsTitle?: boolean;
  titleLabels?: FlipCardTitleLabels;
  /** Visible card topic for the flip control accessible name (CAT-1235). */
  cardTitle?: string;
  /** A question card: its panel edits what the card shows (see Cards3). */
  questionCard?: boolean;
  /** The answer whose feedback the back of a question card shows. */
  answerFeedbackId?: string;
}

export type FlipCardDefinition = ItemDefinition<typeof FLIP_CARD, Props>;

export class FlipCard extends Item<typeof FLIP_CARD, Props> {
  protected validate() {
    const { editorMode } = getEditorState();
    if (editorMode) {
      // In editor mode we allow a simplified version with only front face.
      if (!this.definition.items?.some(i => i.type === FLIP_FRONT)) {
        throw new ItemValidationError(
          `Editor mode FlipCard requires a child of type [${FLIP_FRONT}]`
        );
      }
      return;
    }
    if (this.definition.items?.length !== 2) {
      throw new ItemValidationError(
        `An item of type [${FLIP_CARD}] must have 2 children`
      );
    }

    if (!this.definition.items?.some(i => i.type === FLIP_BACK)) {
      throw new ItemValidationError(
        `An item of type [${FLIP_CARD}] must have a child of type [${FLIP_BACK}]`
      );
    }

    if (!this.definition.items?.some(i => i.type === FLIP_FRONT)) {
      throw new ItemValidationError(
        `An item of type [${FLIP_CARD}] must have a child of type [${FLIP_FRONT}]`
      );
    }
  }

  protected render({
    children,
    width = '100%',
    height = '100%',
    correct,
    id,
    background = 'white',
    type = 'button',
    separateTitleAndBody = false,
    showSubtitle = false,
    showTitle = false,
    cardOwnsTitle = false,
    titleLabels,
    cardTitle,
    questionCard = false,
    answerFeedbackId
  }: RenderProps<Props>) {
    const [playAudioEffect] = useAudioEffect(audioEffect);
    const childArray = Children.toArray(children);

    const front = childArray.find(
      (c: Component<RenderProps<void>>) => c.props.type === FLIP_FRONT
    );
    const back = childArray.find(
      (c: Component<RenderProps<void>>) => c.props.type === FLIP_BACK
    );

    const [flipped, set] = useState(false);
    const [flippedCard, setFlippedCard] = useState(false);
    const hasUserFlipped = useRef(false);
    const frontRef = useRef<HTMLDivElement>(null);
    const backRef = useRef<HTMLDivElement>(null);
    const frontFaceRef = useRef<HTMLDivElement>(null);
    const { transform, opacity } = useSpring({
      opacity: flippedCard ? 1 : 0,
      transform:
        isIPhone13 || isIPad13
          ? `perspective(600px) rotateY(0deg)`
          : `perspective(600px) rotateY(${flippedCard ? 180 : 0}deg)`,
      config: { mass: 5, tension: 500, friction: 80 }
    });

    const { addBlocker, removeBlocker } = useSequenceStore();

    useEffect(() => {
      if ((correct || correct === undefined) && !flipped) {
        addBlocker(id);
        return () => removeBlocker(id);
      }
    }, [correct, flipped]);

    const { editorMode, contentEditable, removeSubItem, addSubItem, sequence } =
      useEditorStore();

    const onClick = (e?: React.MouseEvent) => {
      e?.stopPropagation();
      hasUserFlipped.current = true;
      set(true);
      setFlippedCard(!flippedCard);
      if (!editorMode) {
        try {
          playAudioEffect();
        } catch (error) {
          // Silently ignore audio effect errors
        }
      }
    };

    const [showEditModal, setShowEditModal] = useState(false);
    const canFlip = !contentEditable; // disable flipping when content is editable

    // CAT-1214: keep the hidden face in the DOM for the flip animation, but
    // exclude it from the a11y tree / tab order until the card is flipped.
    // Mirror CarouselItem — set `inert` via DOM (React 18 prop support is uneven).
    useLayoutEffect(() => {
      const backEl = backRef.current;
      const frontEl = frontFaceRef.current;

      const setFaceInert = (el: HTMLElement | null, hidden: boolean) => {
        if (!el) return;
        if (hidden) el.setAttribute('inert', '');
        else el.removeAttribute('inert');
      };

      if (contentEditable) {
        setFaceInert(backEl, false);
        setFaceInert(frontEl, false);
        return;
      }

      setFaceInert(backEl, flippedCard);
      setFaceInert(frontEl, !flippedCard);
    }, [flippedCard, contentEditable]);

    useEffect(() => {
      if (!canFlip || !hasUserFlipped.current) return;
      const el = flippedCard ? frontRef.current : backRef.current;
      el?.focus();
    }, [canFlip, flippedCard]);

    const {
      onDragStart,
      onDrop,
      onDragOver,
      onDragEnter,
      onDragEnd,
      onPointerDown
    } = useReorderCards();

    const handleDuplicate = () => {
      if (!sequence) return;
      const parentId = sequence.id;
      const existingCardPositions = (sequence.items || [])
        .filter((it: any) => it.__typename === 'Card')
        .map((it: any) => (typeof it.position === 'number' ? it.position : 0));

      if (existingCardPositions.length >= 9) {
        useToastStore.getState().add({
          severity: ToastSeverity.Error,
          message: 'You can only add up to 9 cards'
        });

        return; // silently ignore if limit reached
      }

      const position =
        (existingCardPositions.length
          ? Math.max(...existingCardPositions)
          : 0) + 1;

      const sourceCard: any = sequence.items.find(
        (it: any) => String(it.id) === String(id)
      );

      const titleVal =
        sourceCard?.title ||
        (front as any)?.props?.children?.props?.value ||
        '';

      const subtitleVal = sourceCard?.subtitle || '';

      const bodyVal = sourceCard?.body || '';
      const pictures = Array.isArray(sourceCard?.pictures)
        ? (sourceCard.pictures || []).filter(
            (p: any) => p && (p.mediaUrl || p.url || p.id)
          )
        : [];

      const tempId = `temp-${Date.now()}`;

      addSubItem({
        sequenceId: parentId,
        data: {
          type: 'CARD',
          title: titleVal,
          value: titleVal,
          subtitle: subtitleVal || undefined,
          position,
          parent_id: null
        },
        newItem: {
          id: tempId,
          position,
          title: titleVal,
          subtitle: subtitleVal || undefined,
          body: bodyVal,
          correct: false,
          pictures: pictures.length ? pictures.map((p: any) => ({ ...p })) : []
        },
        onCreated: async (serverItem: ServerItemResponse) => {
          const realId = serverItem.subitem.id;
          const state = getEditorState();
          const api = state.apiService;
          try {
            if (bodyVal) {
              state.updateSequence(realId, 'body', bodyVal);
            }
            if (subtitleVal) {
              state.updateSequence(realId, 'subtitle', subtitleVal);
            }
            if (api && pictures.length) {
              // only persist pictures if there are valid ids
              const validIds = pictures.map((p: any) => p.id).filter(Boolean);
              if (validIds.length) {
                await api.updateSubItem(realId, 'picture_ids', validIds);
              }
            }
          } catch (e) {
            console.error('Duplicate card persistence failed', e);
          }
        }
      });
    };

    const isListItem = type === 'li';

    // CAT-1235 / WCAG 2.4.6: name the flip control by card topic, not bare "Open details".
    const titleFromSequence = (
      sequence?.items as
        Array<{ id?: string | number; title?: string }> | undefined
    )?.find(it => String(it.id) === String(id))?.title;
    const openerLabel =
      stripHtml(cardTitle || titleFromSequence || '').trim() || t`Open details`;

    /** Button semantics + flip handlers; go on the li's inner wrapper.
     * Flip on capture so nested Card `stopPropagation` cannot block mouse flips
     * (a11y). Do not use pointer-events:none on faces — that breaks text scroll. */
    const interactiveProps = {
      tabIndex: 0,
      role: contentEditable ? undefined : 'button',
      'aria-label': openerLabel,
      'aria-expanded': canFlip ? flippedCard : undefined,
      onClickCapture: canFlip
        ? (e: React.MouseEvent) => {
            const target = e.target as HTMLElement;
            // Scrollable body must keep pointer events (wheel / scrollbar).
            if (target.closest('[data-flip-scroll]')) return;
            // Skip nested action buttons (edit/duplicate/delete). When the
            // flip control itself is a <button> (SocialMedia12), closest()
            // matches currentTarget — that must still flip.
            const nestedBtn = target.closest('button');
            if (nestedBtn && nestedBtn !== e.currentTarget) return;
            // Skip links in card HTML so capture-phase flip does not steal
            // the click before navigation (Bugbot / CAT-1235 follow-up).
            if (target.closest('a[href]')) return;
            onClick();
          }
        : undefined,
      onKeyDown: (e: React.KeyboardEvent) => {
        if (canFlip && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }
    };

    const cardContent = (
      <>
        {contentEditable && (
          <div
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              display: 'flex',
              gap: 6,
              zIndex: 10,
              // Removed background so action icons have no backdrop
              background: 'transparent',
              borderRadius: 0,
              padding: 0
            }}
          >
            <button
              type="button"
              aria-label={t`Edit card`}
              style={{
                background: 'transparent',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                display: 'flex'
              }}
              onClick={() => setShowEditModal(true)}
            >
              <EditIcon width={16} height={16} />
            </button>
            <button
              type="button"
              aria-label={t`Duplicate card`}
              style={{
                background: 'transparent',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                display: 'flex'
              }}
              onClick={handleDuplicate}
            >
              <DuplicateIcon width={16} height={16} />
            </button>
            <button
              type="button"
              aria-label={t`Delete card`}
              style={{
                background: 'transparent',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                display: 'flex'
              }}
              onClick={() => removeSubItem(id)}
            >
              <TrashIcon width={16} height={16} />
            </button>
          </div>
        )}
        <AnimatedCard
          ref={backRef}
          tabIndex={canFlip ? -1 : undefined}
          aria-hidden={flippedCard || undefined}
          style={{
            overflow: 'hidden',
            gridRow: 1,
            gridColumn: 1,
            opacity: opacity.to(o => 1 - o),
            transform,
            backgroundColor: background
          }}
        >
          {back}
        </AnimatedCard>
        <FrontWrapper
          ref={frontFaceRef}
          aria-hidden={!flippedCard || undefined}
          correct={(correct && correct.toString()) || undefined}
          style={{
            opacity,
            transform,
            rotateY: isIPhone13 || isIPad13 ? '0deg' : '180deg',
            cursor: canFlip ? 'pointer' : undefined
          }}
        >
          <FrontCard
            ref={frontRef}
            tabIndex={canFlip ? -1 : undefined}
            correct={correct}
          >
            {front}
          </FrontCard>
        </FrontWrapper>
        {showEditModal && (
          <FlipCardEditModal
            open={showEditModal}
            onClose={() => setShowEditModal(false)}
            cardId={id}
            separateTitleAndBody={separateTitleAndBody}
            showSubtitle={showSubtitle}
            showTitle={showTitle}
            cardOwnsTitle={cardOwnsTitle}
            titleLabels={titleLabels}
            questionCard={questionCard}
            answerFeedbackId={answerFeedbackId}
          />
        )}
      </>
    );

    return (
      <StyledCard
        editor={contentEditable}
        width={width}
        height={height}
        as={type as never}
        {...(isListItem ? {} : interactiveProps)}
        style={{
          position: 'relative',
          cursor: canFlip ? 'pointer' : undefined
        }}
        draggable={contentEditable}
        onDragStartCapture={e => {
          if (!contentEditable) {
            e.preventDefault();
          }
        }}
        onPointerDown={e => {
          if (showEditModal) return; // disable drag while modal open
          if (contentEditable) onPointerDown(String(id), e);
        }}
        data-card-id={id}
        onDragStart={
          contentEditable ? e => onDragStart(String(id), e) : undefined
        }
        onDrop={contentEditable ? () => onDrop() : undefined}
        onDragOver={contentEditable ? onDragOver : undefined}
        onDragEnter={
          contentEditable ? () => onDragEnter(String(id)) : undefined
        }
        onDragEnd={contentEditable ? onDragEnd : undefined}
      >
        {isListItem ? (
          <CardInteractive
            {...interactiveProps}
            style={canFlip ? { cursor: 'pointer' } : undefined}
          >
            {cardContent}
          </CardInteractive>
        ) : (
          cardContent
        )}
      </StyledCard>
    );
  }
}

registry[FLIP_CARD] = FlipCard;
